// src/services/pagibigContributionTable.js

/**
 * Client-side reader + resolver for the GLOBAL `contributionTables/pagibig`
 * collection published by the platform app (BL-062). This is the sahod-app
 * consumer (BL-074): it powers the salary-derived Pag-IBIG suggestion chip at
 * employee setup — the third sibling of sssContributionTable.js (BL-063) and
 * philhealthContributionTable.js (BL-073).
 *
 * The engine never does a live Pag-IBIG lookup — payroll reads the frozen
 * `employment.pagibigContribution`. The schedule only matters at ONE moment:
 * employee setup, deciding what to SUGGEST for that saved figure. So this service
 * is read-only and suggestion-only; it never writes.
 *
 * Pag-IBIG is a small rate-tier schedule with a hard salary cap:
 *   base = min(fundSalary, maxFundSalary)   // clamp FIRST, before any rate
 *   band = the FIRST tier whose compMax is null or >= base
 *   ee   = round2(base × band.eeRate)
 *   er   = round2(base × band.erRate)
 *
 * Two things that differ from the siblings and are easy to get wrong:
 *  - Band selection is "first compMax null or >= base", NOT a containment test.
 *    The one-centavo contiguity rule makes the stored bands [0,1500] ∪ [1500.01,∞),
 *    so a containment test leaves (1500, 1500.01) matching nothing.
 *  - Employee and employer each carry their own rate, so each side is rounded
 *    INDEPENDENTLY — there is no split and no centavo remainder, unlike
 *    PhilHealth's round-then-split.
 *
 * Basis caveat (C13): HDMF defines "Fund Salary" as basic salary PLUS allowances,
 * but Sahod has no allowances field at employee setup, so the caller passes the
 * shared basic-salary monthly figure (`monthlyGrossForEmployee`). The chip copy
 * discloses this and the field stays editable. In practice the ₱10,000 cap
 * absorbs the difference for nearly everyone.
 *
 * Fallback / fail-closed: returns null (→ no chip, manual entry) when no version
 * resolves, the schedule is malformed, or the salary can't resolve.
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { round2 } from './contributionTableClient';

/** @param {*} v @returns {boolean} true iff v is a finite number. */
function isFiniteNum(v) {
  return typeof v === 'number' && isFinite(v);
}

/**
 * Read every published Pag-IBIG contribution-table version. Cardinality is a
 * handful per decade, so read them all and resolve in memory. Each returned
 * object is the stored version doc with `effectiveDate` taken from the doc id.
 *
 * @returns {Promise<Array<Object>>} version docs (may be empty)
 */
export async function fetchPagibigTableVersions() {
  const snap = await getDocs(
    collection(db, 'contributionTables', 'pagibig', 'versions')
  );
  return snap.docs.map((d) => ({ effectiveDate: d.id, ...d.data() }));
}

/**
 * The employee + employer Pag-IBIG shares for a fund salary against an in-force
 * version. Clamps to `maxFundSalary` FIRST, then selects the first tier whose
 * `compMax` is null or `>= base`, then rounds each side independently.
 *
 * Returns the FULL result — the suggestion chip renders `ee`, while the
 * derivation popover needs `base` (to show the cap note) and `band` (to name the
 * selected rate). Re-deriving either in the component would put the band rule in
 * a second place.
 *
 * FAILS CLOSED — returns null when the version is missing, `maxFundSalary` is
 * unusable, `tiers` is missing/empty, **any tier is structurally malformed**, or
 * the fund salary is unusable. A legitimate ₱0.00 (a zero fund salary) computes 0
 * and is returned, not nulled.
 *
 * @param {?Object} version - an in-force version doc (with maxFundSalary + tiers)
 * @param {number} fundSalary - monthly fund salary in pesos
 * @returns {?{ee: number, er: number, base: number, band: Object}} or null
 */
export function computePagibigShares(version, fundSalary) {
  if (!version) return null;
  const { maxFundSalary, tiers } = version;
  if (!isFiniteNum(maxFundSalary) || maxFundSalary <= 0) return null;
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  if (!isFiniteNum(fundSalary) || fundSalary < 0) return null;

  // Every tier is validated BEFORE the band is selected. Doing this inside the
  // selection predicate is NOT fail-closed: the last tier carries
  // `compMax === null` and therefore always matches, so a predicate that merely
  // *skips* a malformed tier lets the scan walk past a corrupt low band and land
  // on the open-ended top one — silently promoting the employee to a higher rate.
  // That yields a plausible-looking wrong figure (e.g. ₱20.00 where ₱10.00 is
  // correct) which an operator would click, and a wrong number is worse than no
  // chip.
  //
  // Scope of this loop, stated precisely: it mirrors the server's per-tier VALUE
  // rules (contributionTableShape.js `validatePagibigTier`) — enough that no band
  // it selects can produce a garbage figure. It deliberately does NOT re-check the
  // server's table-level STRUCTURE (contiguity, `tiers[0].compMin === 0`,
  // `finalTier.compMin <= maxFundSalary`) or the exact-four-key set: the compute
  // does not depend on any of them, and duplicating the whole validator here is
  // what CLAUDE.md #2 warns against. So a hand-edited doc with, say, a gap between
  // bands still returns a figure rather than null — structure is trusted from the
  // publish path, which is the only writer.
  const rateOk = (r) => isFiniteNum(r) && r > 0 && r <= 1;
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    const isLast = i === tiers.length - 1;
    if (!t || typeof t !== 'object' || Array.isArray(t)) return null;
    if (!isFiniteNum(t.compMin) || t.compMin < 0) return null;
    if (isLast ? t.compMax !== null : !isFiniteNum(t.compMax)) return null;
    if (!rateOk(t.eeRate) || !rateOk(t.erRate)) return null;
  }

  const base = Math.min(fundSalary, maxFundSalary);

  // First tier whose compMax is null (open-ended) or >= base — NOT a containment
  // test, which would fall through the one-centavo gap between bands.
  const band = tiers.find((t) => t.compMax === null || t.compMax >= base);
  // Unreachable on a schedule that passed the loop above (the last tier's null
  // compMax always matches); kept as a cheap invariant guard.
  if (!band) return null;

  return {
    ee: round2(base * band.eeRate),
    er: round2(base * band.erRate),
    base,
    band,
  };
}
